import { clamp01, smoothstep } from './math';

/**
 * Whitecapping model: how much foam is on the water right now.
 *
 * Deep-water form, no depth or current. Two onset mechanisms, and we take the
 * stronger of the two:
 *
 * - **Wind-driven** — Monahan & O'Muircheartaigh (1980): W = 3.84e-6 × U10^3.41,
 *   with whitecap inception around 3 m/s. We keep W (the true whitecap area
 *   fraction) for a readout, and ramp the *visual* density from the 3 m/s onset
 *   up to about 16 m/s.
 * - **Steepness** — deep-water wavelength L₀ = gT²/2π ≈ 1.56T². A wave steepens
 *   toward the Michell breaking limit H/L ≈ 1/7. Wind blowing against the swell
 *   raises the effective steepness, so foam comes on sooner. Period is floored
 *   so a missing swell (height and period 0) never divides by zero.
 *
 * `intensity` is what the render reads; the rest is exposed so the model can be
 * inspected and tested.
 */
export interface Whitecaps {
  /** 0 to 1. Drives how much foam is drawn. */
  intensity: number;
  /** Monahan whitecap area fraction, the true physical value. */
  areaFraction: number;
  /** Effective wave steepness H/L, raised by an opposing wind. */
  steepness: number;
  /** The wind-driven contribution to `intensity` alone. */
  windOnset: number;
  /** The steepness contribution to `intensity` alone. */
  steepnessOnset: number;
}

/** Whitecaps do not form below this wind speed, m/s. */
const MONAHAN_INCEPTION_MPS = 3;

const MONAHAN_COEFFICIENT = 3.84e-6;
const MONAHAN_EXPONENT = 3.41;

/** The Michell limit: a deep-water wave breaks at about H/L = 1/7. */
const BREAKING_LIMIT = 1 / 7;

/**
 * Floor for wave period when computing wavelength. Keeps H/L defined when the
 * scene passes a missing swell as period 0.
 */
const MIN_WAVE_PERIOD_S = 0.6;

export const calculateWhitecaps = (
  windSpeedMps: number,
  windDirectionDeg: number,
  waveHeightM: number,
  wavePeriodS: number,
  swellDirectionDeg: number,
): Whitecaps => {
  const areaFraction =
    windSpeedMps > MONAHAN_INCEPTION_MPS
      ? MONAHAN_COEFFICIENT * windSpeedMps ** MONAHAN_EXPONENT
      : 0;

  const wavelengthM = 1.56 * Math.max(wavePeriodS, MIN_WAVE_PERIOD_S) ** 2;
  const oppose = clamp01(
    -Math.cos(((windDirectionDeg - swellDirectionDeg) * Math.PI) / 180),
  );
  const steepen = 1 + oppose * smoothstep(4, 14, windSpeedMps) * 0.5;
  const steepness = (waveHeightM / wavelengthM) * steepen;

  const windOnset = smoothstep(MONAHAN_INCEPTION_MPS, 16, windSpeedMps);
  const steepnessOnset = smoothstep(0.045, BREAKING_LIMIT, steepness);

  return {
    intensity: Math.max(windOnset, steepnessOnset),
    areaFraction,
    steepness,
    windOnset,
    steepnessOnset,
  };
};
